import { ReactNode, useEffect, useState } from "react";

import { useFilesContext } from "../../../../context/myFilesContext";
import {
  FileListColumnFilterable,
  FileListColumnSortable,
  FileListRequest,
  IFileSchema,
} from "../../../../services/Endpoints/files/FileListEndpoints";
import { useProtectedAxiosInstance } from "../../../../hooks/axiosInstance";
import { FileStatusEnum } from "../../../../services/Schemas/File";
import { useNotificationContext } from "../../../../context/notificationContext";
import errorResolver from "../../../../utils/error-resolver";
import { Box, Stack, Tab } from "@mui/material";
import { TabContext } from "@mui/lab";
import { useLangContext } from "../../../../context/langContext";
import {
  FilterOperatorEnum,
  SortDirectionEnum,
} from "../../../../services/Schemas/ListQueryString";
import MuiTabPanel from "../../../common/tab-panel/TabPanel";
import CustomTable from "../../../common/tables/Table";
import { dateFormatter } from "../../../../utils/date-formatter";
import ViewAction from "../../../common/view-action/ViewAction";
import InfoBadge, { OriginEnum } from "../../../common/info-badge/InfoBadge";
import UserLink from "../../common/UserLink";
import { useUrl } from "../../../../hooks/urlEncoding";
import LinkWithPopover from "../../common/LinkWithPopover";
import { Link, NavLink, useLocation } from "react-router-dom";
import { routerPaths } from "../../../../constants/Paths";
import { useCanAccess } from "../../../../hooks/canAccess";
import { AccessEnums } from "../../../../constants/accessEnum";
import { Profile2User } from "iconsax-react";
import {
  UsersListColumnFilterable,
  UsersListColumnSortable,
} from "../../../../services/Endpoints/users/UsersListEndpoint";
import { ITableSchema } from "../../../../utils/types";
import NumberFilter from "../../../common/tables/filters/NumberFilter";
import useTableSchema from "../../../../hooks/useTableSchema";
import TextFilter from "../../../common/tables/filters/TextFilter";
import DateTimeFilter from "../../../common/tables/filters/DateTimeFilter";
import SelectionFilter from "../../../common/tables/filters/SelectionFilter";
import useUrlSort from "../../../../hooks/useUrlSort";

const compInfos = {
  en: {
    tabs: [
      { id: "1", label: "User Profile" },
      { id: "2", label: "Password Change" },
      { id: "3", label: "Devices" },
    ],
  },
  fa: {
    tabs: [
      { id: "1", label: "همه فایل ها" },
      { id: "3", label: "در انتظار بررسی" },
      { id: "2", label: "تایید شده" },
      { id: "4", label: "رد شده" },
    ],
  },
};
const statusOptions = {
  [FileStatusEnum.PENDING]: "در انتظار بررسی",
  [FileStatusEnum.VERIFIED]: "تایید شده",
  [FileStatusEnum.REJECTED]: "رد شده",
};

interface IData {
  id: string;
  mobile: ReactNode;
  name: string | null;
  created_at: string;
  operations: ReactNode;
  verifying_status: ReactNode;
  size: string;
  file_duration: string;
}

export default function UserFilesTableComponent() {
  const { queryStringUrl, parseQueryString, setParamWithSearchParams } =
    useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 25;
  const [page, setPage] = useState(initialPage);
  const [value, setValue] = useState("1");
  const [data, setData] = useState<IData[] | []>([]);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);
  const [waiting, setWaiting] = useState(false);
  const [total, setTotal] = useState(0);
  const { fileReload, setCurrentFile, setFileReload } = useFilesContext();
  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();
  const { lang } = useLangContext();
  const location = useLocation();
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();
  const userFilesColumns: ITableSchema[] = [
    {
      key: "id",
      label: "شناسه",
      minWidth: 100,
      align: "center",
      FilterComponent: NumberFilter,
      sortable: true,
    },
    {
      key: "name",
      label: "نام",
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "type",
      label: "نوع فایل",
      minWidth: 170,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "size",
      label: "سایز",
      minWidth: 170,
      align: "center",
      FilterComponent: NumberFilter,
      format: (value) => `MB ${(Number(value) / (1024 * 1024)).toFixed(2)}`,
    },
    {
      key: "file_duration",
      label: "مدت صوت",
      minWidth: 170,
      align: "center",
      FilterComponent: NumberFilter,
      format: (value) => `${value} ثانیه`,
    },
    {
      key: "mobile",
      label: "موبایل",
      minWidth: 170,
      align: "center",
    },

    {
      key: "created_at",
      label: "تاریخ ایجاد",
      minWidth: 100,
      align: "center",
      FilterComponent: DateTimeFilter,
      format: (value: string) =>
        value && dateFormatter({ lang: "fa", date: value }),
    },
    {
      key: "verifying_status",
      label: "وضعیت",
      minWidth: 150,
      align: "center",
      FilterComponent: SelectionFilter,
      options: statusOptions,
    },
    {
      key: "operations",
      label: "عملیات",
      minWidth: 170,
      align: "center",
    },
  ];

  const { filtersSchema, columns } = useTableSchema(userFilesColumns);

  const handleView = (file: IFileSchema) => {
    setCurrentFile(file);
  };

  const { can } = useCanAccess();
  const infos = compInfos[lang];

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };


  const getData = async () => {
    try {
      setWaiting(true);
      const fileListRequest = new FileListRequest(protectedInstance);
      const res = await fileListRequest.send({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      // setData(res.data.files);

      const transactions = res.data.files.map((file) => ({
        size: file.size,
        type: file.type,
        file_duration: file.length,
        id: file.id,
        mobile: file.user && (
          <UserLink
            disabled={!can(AccessEnums.AdminUsersList)}
            user={{
              name: file.user.name,
              mobile: file.user.mobile,
              id: file.user.id,
            }}
          />
        ),
        name: file.name,
        created_at: file.created_at,
        verifying_status:
          file.verifying_status == "rejected" ? (
            <LinkWithPopover
              title={
                file.activities.filter((itm) => itm.action == "rejected")[0]
                  .description ?? ""
              }
            >
              <InfoBadge
                origin={OriginEnum.FILE}
                status={file.verifying_status}
              />
            </LinkWithPopover>
          ) : (
            <InfoBadge
              origin={OriginEnum.FILE}
              status={file.verifying_status}
            />
          ),
        operations: (
          <Stack justifyContent="center" alignItems="center" direction="row">
            <ViewAction handleView={() => handleView(file)} />

            {file.user && (
              <LinkWithPopover
                disabled={!can(AccessEnums.AdminPbxCallDetailRecordsList)}
                title="کاربران"
              >
                <Stack
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    width: "fit-content",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                      // backgroundColor: (theme) => theme.palette.others.doneBg,
                    },
                  }}
                  component={Link}
                  to={queryStringUrl(routerPaths.USERS, {
                    filters: {
                      [UsersListColumnFilterable.ID]: {
                        column: UsersListColumnSortable.ID,
                        operator: FilterOperatorEnum.EQUAL,
                        value: file.user.id,
                      },
                    },
                    sorts: {
                      [UsersListColumnSortable.ID]: {
                        column: UsersListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <Profile2User size="20" />
                </Stack>
              </LinkWithPopover>
            )}
          </Stack>
        ),
      }));
      setTotal(res.data.total ?? 0);
      setData(transactions);

      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getData();
  }, [fileReload, page, rowsPerPage, value, location.search]);

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <CustomTable
        reloadAction={() => setFileReload(!fileReload)}
        hasWaiting={waiting}
        count={total}
        hasTopPagination
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rows={data}
        rowsPerPage={rowsPerPage}
        columns={columns}
        hasSearchFilter
        filtersSchema={filtersSchema}
        onApplySorts={applyUrlSorts}
        sorts={sorts}
      />
    </Box>
  );
}
